import { environment } from 'environment';

const Prefix = {
  Identity:
    environment.mode !== 'local'
      ? `${environment.service.host}/identity`
      : 'https://localhost:5101',
  Master:
    environment.mode !== 'local'
      ? `${environment.service.host}/master`
      : 'http://localhost:5500',
  Configuration:
    environment.mode !== 'local'
      ? `${environment.service.host}/configuration`
      : 'http://localhost:5600',
  Customer:
    environment.mode !== 'local'
      ? `${environment.service.host}/customer`
      : 'http://localhost:5900',
  Transaction:
    environment.mode !== 'local'
      ? `${environment.service.host}/order`
      : 'http://apigw-dev.xbaht.com/order',
  Log:
    environment.mode !== 'local'
      ? `${environment.service.host}/logger`
      : 'http://localhost:5700',
  IdentityConnect:
    environment.mode !== 'local'
      ? `${environment.service.host}/identity-connect`
      : 'https://localhost:5961',
  // BO-945 — reporting service (Riksbank MREM reporting, BO-935). Local port 5900
  // matches Sources/Presentation/Dockerfile-pipeline's ASPNETCORE_URLS exactly —
  // collides with Customer's own local port above; both already existed before this
  // change (Customer's local dev value, reporting's real Dockerfile config), not
  // introduced here. Flagged, not fixed — a local-dev-only concern, unaffected by any
  // real gateway-routed environment.
  Reporting:
    environment.mode !== 'local'
      ? `${environment.service.host}/reporting`
      : 'http://localhost:5900',
};

export const Endpoint = {
  Identity: {
    Authentication: {
      SignIn: `${Prefix.Identity}/authentication/signin`,
      AuthenticatorKey: `${Prefix.Identity}/authentication/authenticator-key`,
      AuthenticatorCode: `${Prefix.Identity}/authentication/authenticator-code`,
      Verify: `${Prefix.Identity}/authentication/verify`,
      Permission: `${Prefix.Identity}/authentication/permission`,
      Token: `${Prefix.Identity}/authentication/token`,
      ResetPassword: `${Prefix.Identity}/authentication/resetpassword`,
      ForgotPassword: `${Prefix.Identity}/authentication/forgotpassword`,
      SignOut: `${Prefix.Identity}/authentication/signout`,
      Me: `${Prefix.Identity}/authentication/me`,
      AuthenticatorReactivateVerify: `${Prefix.Identity}/authentication/authenticator-reactivate/verify`,
      AuthenticatorReactivateActivate: `${Prefix.Identity}/authentication/authenticator-reactivate/activate`,
    },
    User: {
      Common: `${Prefix.Identity}/user`,
      Current: `${Prefix.Identity}/user/current`,
      History: `${Prefix.Identity}/user/history`,
    },
    Role: {
      Common: `${Prefix.Identity}/role`,
      Lookup: `${Prefix.Identity}/role/lookup`,
      History: `${Prefix.Identity}/role/history`,
    },
  },
  Master: {
    Claim: {
      Common: `${Prefix.Master}/claim`,
    },
    Country: {
      Common: `${Prefix.Master}/country`,
    },
    Currency: {
      Common: `${Prefix.Master}/currency`,
    },
    PayoutMethod: {
      Common: `${Prefix.Master}/payoutmethod`,
    },
    Partner: {
      Common: `${Prefix.Master}/partner`,
    },
    Expression: {
      Common: `${Prefix.Master}/expression`,
    },
    PaymentMethod: {
      Common: `${Prefix.Master}/paymentmethod`,
    },
    RiskLevel: {
      Common: `${Prefix.Master}/risklevel`,
    },
    Occupation: {
      Common: `${Prefix.Master}/occupation`,
    },
    MonthlyPayment: {
      Common: `${Prefix.Master}/monthlypayment`,
    },
    Purpose: {
      Common: `${Prefix.Master}/purpose`,
    },
    Regularity: {
      Common: `${Prefix.Master}/regularity`,
    },
    CustomerTag: {
      Common: `${Prefix.Master}/customertag`,
    },
    Bank: {
      Common: `${Prefix.Master}/bank`,
    },
    Branch: {
      Common: `${Prefix.Master}/bank/{id}/branch`,
    },
    UpdateRequest: {
      Common: `${Prefix.Master}/updaterequest`,
    },
    TimeRequest: {
      Common: `${Prefix.Master}/timerequest`,
    },
    DocumentRequest: {
      Common: `${Prefix.Master}/documentrequest`,
    },
  },
  Configuration: {
    Country: {
      Common: `${Prefix.Configuration}/country`,
      History: `${Prefix.Configuration}/country/history`,
    },
    ExchangeRate: {
      Common: `${Prefix.Configuration}/exchangerate`,
      History: `${Prefix.Configuration}/exchangerate/history`,
    },
    TransactionFee: {
      Common: `${Prefix.Configuration}/transactionfee`,
      History: `${Prefix.Configuration}/transactionfee/history`,
    },
    SystemLimit: {
      Common: `${Prefix.Configuration}/systemlimit`,
      History: `${Prefix.Configuration}/systemlimit/history`,
    },
    PayoutPartner: {
      Common: `${Prefix.Configuration}/payoutpartner`,
      History: `${Prefix.Configuration}/payoutpartner/history`,
    },
    Campaign: {
      Common: `${Prefix.Configuration}/campaign`,
      History: `${Prefix.Configuration}/campaign/history`,
    },
    Application: {
      Common: `${Prefix.Configuration}/application`,
      History: `${Prefix.Configuration}/application/history`,
    },
    Notification: {
      Common: `${Prefix.Configuration}/notification`,
      History: `${Prefix.Configuration}/notification/history`,
    },
  },
  Transaction: {
    Dashboard: {
      OrderSummary: `${Prefix.Transaction}/dashboard/order-summary`,
      PayoutSummary: `${Prefix.Transaction}/dashboard/payout-summary`,
    },
    Order: {
      Common: `${Prefix.Transaction}`,
      Orders: `${Prefix.Transaction}/orders`,
      PatIn: `${Prefix.Transaction}/{orderNo}/payins`,
      PatOut: `${Prefix.Transaction}/{orderNo}/payouts`,
      CancelPayout: (orderNo: string, payoutDocNo: string) =>
        `${Prefix.Transaction}/${orderNo}/payouts/${payoutDocNo}/cancel`,
      Customer: `${Prefix.Transaction}/customer`,
      Export: `${Prefix.Transaction}/export`,
      ExportRequest: `${Prefix.Transaction}/export/request`,
      Resend: `${Prefix.Transaction}/resend`,
      Verify: `${Prefix.Transaction}/verify`,
      Refund: `${Prefix.Transaction}/refund`,
      History: `${Prefix.Transaction}/history`,
    },
  },
  Customer: {
    Dashboard: {
      CustomerSummary: `${Prefix.Customer}/dashboard/customer-summary`,
      ComplianceSummary: `${Prefix.Customer}/dashboard/compliance-summary`,
    },
    Customer: {
      Common: `${Prefix.Customer}/customer`,
      Export: `${Prefix.Customer}/customer/export`,
      History: `${Prefix.Customer}/customer/history`,
    },
    ComplianceRedFlag: {
      Common: `${Prefix.Customer}/complianceredflag`,
      Group: `${Prefix.Customer}/complianceredflag/group`,
    },
    ComplianceKyc: {
      Common: `${Prefix.Customer}/compliancekyc`,
      Batch: `${Prefix.Customer}/compliancekyc/batch`,
    },
    Compliance: {
      Common: `${Prefix.Customer}/compliance`,
      Request: `${Prefix.Customer}/compliance/request`,
      Latest: `${Prefix.Customer}/compliance/latest`,
      Cancel: `${Prefix.Customer}/compliance/cancel`,
      Upload: `${Prefix.Customer}/compliance/upload`,
      Close: (requestNo: string) =>
        `${Prefix.Customer}/compliance/${encodeURIComponent(requestNo)}/close`,
    },
    ComplianceInformation: {
      Common: `${Prefix.Customer}/complianceinformation`,
    },
    ComplianceDocument: {
      Common: `${Prefix.Customer}/compliancedocument`,
    },
    Recipient: {
      Common: `${Prefix.Customer}/recipient`,
      History: `${Prefix.Customer}/recipient/history`,
    },
    DocumentNote: {
      Common: `${Prefix.Customer}/documentnote`,
    },
    DocumentHistory: {
      Common: `${Prefix.Customer}/documenthistory`,
      KycHistory: `${Prefix.Customer}/documenthistory/kyc-history`,
    },
    Metabase: {
      Dataset: `${Prefix.Customer}/metabase/dataset`,
    },
    BFF: {
      CustomerToken: `${Prefix.Customer}/bff/customer/token`,
    },
  },
  Log: {
    History: {
      Common: `${Prefix.Log}/history`,
      Lookup: `${Prefix.Log}/history/lookup`,
    },
  },
  IdentityConnect: {
    IDkollen: {
      Common: `${Prefix.IdentityConnect}/history`,
      Authentication: `${Prefix.IdentityConnect}/idkollen/bankid/authentication`,
      Collect: `${Prefix.IdentityConnect}/idkollen/bankid/collect`,
      Cancel: `${Prefix.IdentityConnect}/idkollen/bankid/cancel`,
    },
  },
  // "riksbank-" prefix is deliberate — see ReportingPeriodsController's own comment
  // (reporting service is expected to grow non-Riksbank reporting features later).
  Reporting: {
    Period: {
      Common: `${Prefix.Reporting}/riksbank-reporting-periods`,
      Current: `${Prefix.Reporting}/riksbank-reporting-periods/current`,
      Runs: (periodKey: string) =>
        `${Prefix.Reporting}/riksbank-reporting-periods/${periodKey}/runs`,
      Detail: (periodKey: string) =>
        `${Prefix.Reporting}/riksbank-reporting-periods/${periodKey}`,
      // BO-992 — addresses ANY attempt including Superseded ones, not only the latest.
      Payload: (periodKey: string, attemptNo: number) =>
        `${Prefix.Reporting}/riksbank-reporting-periods/${periodKey}/attempts/${attemptNo}/payload`,
    },
  },
};
